<script lang="ts" setup>
import BillingNotice from '@/components/business/members/BillingNotice.vue'
import SeatDowngradeNotice from '@/components/business/members/SeatDowngradeNotice.vue'
import SimpleBaseDialog from '@/components/dialogs/SimpleBaseDialog.vue'
import type { BillingNoticeProps } from '#shared/types/billingNotice'

const props = withDefaults(
  defineProps<{
    mode: 'upgrade' | 'downgrade'
    isBulk?: boolean
    memberName?: string
    count?: number
    context?: 'seat_change' | 'pending_invitation'
    loading?: boolean
  }>(),
  { isBulk: false, memberName: '', count: 1, context: 'seat_change', loading: false }
)

const emit = defineEmits<{
  action: [contextData: Dialogs['business/DialogSeatChange']['contextData']]
  close: []
}>()

const { t } = useI18n()
const { id: businessId } = storeToRefs(useBusinessStore())
const widgetStore = useWidgetStore()
const { logError } = useErrorHandling()
const { fetchSeatStats } = useSeatStatsFetch(businessId)
const { getBreakdown } = useSeatUpgradeBreakdown(businessId)

const isUpgrade = props.mode === 'upgrade'

const billing = ref<BillingNoticeProps | null>(null)
const isResolvingSeats = computed(() => isUpgrade && !billing.value)

const resolveBilling = async () => {
  try {
    billing.value = getBreakdown(await fetchSeatStats(), props.count)
  } catch (error) {
    logError('Failed to read seat stats for the seat-change dialog', error)
    widgetStore.createSnackbar({ message: t('global.general_error'), color: 'error' })
    emit('close')
  }
}

onMounted(() => {
  if (isUpgrade) void resolveBilling()
})

const { trackAbandoned } = useBillingNoticeTracking(() =>
  billing.value
    ? { context: props.context, fromSeat: 'signer', toSeat: 'full', memberCount: props.count, breakdown: billing.value }
    : undefined
)

const close = () => {
  trackAbandoned()
  emit('close')
}

const icon = computed(() => (isUpgrade ? 'custom:seat_full' : 'custom:seat_signer'))
const titleKeypath = computed(() =>
  isUpgrade ? 'business.members.seat_change.upgrade.title' : 'business.members.seat_change.downgrade.title'
)
const dialogTitle = computed(() => {
  if (props.isBulk) {
    return isUpgrade
      ? t('business.members.seat_change.upgrade.title_bulk', { count: props.count }, props.count)
      : t('business.members.seat_change.downgrade.title_bulk', { count: props.count }, props.count)
  }
  return isUpgrade
    ? t('business.members.seat_change.upgrade.title', { name: props.memberName })
    : t('business.members.seat_change.downgrade.title', { name: props.memberName })
})
const description = computed(() => {
  if (isUpgrade) {
    return props.isBulk
      ? t('business.members.seat_change.upgrade.description_bulk')
      : t('business.members.seat_change.upgrade.description')
  }
  return props.isBulk
    ? t('business.members.seat_change.downgrade.description_bulk')
    : t('business.members.seat_change.downgrade.description')
})
const actionText = computed(() => {
  if (!isUpgrade) return t('business.members.seat_change.downgrade.confirm', props.count)
  return billing.value && billing.value.newlyPurchased > 0
    ? t('business.members.seat_change.upgrade.confirm_pay', props.count)
    : t('business.members.seat_change.upgrade.confirm', props.count)
})
</script>

<template>
  <simple-base-dialog
    :title="dialogTitle"
    :action-text="actionText"
    :cancel-text="t('global.not_now')"
    cancel-color="grey-dark"
    max-width="580"
    :loading="loading"
    :is-content-loading="isResolvingSeats"
    data-cy="seat_change_dialog"
    @action="emit('action', { newlyPurchased: billing?.newlyPurchased ?? 0 })"
    @close="close"
  >
    <template #header>
      <div class="seat-change-icon" :class="`seat-change-icon--${mode}`">
        <v-icon size="28">{{ icon }}</v-icon>
      </div>
    </template>
    <template v-if="!isBulk" #title>
      <i18n-t :keypath="titleKeypath" tag="span">
        <template #name>
          <span class="seat-change-name">{{ memberName }}</span>
        </template>
      </i18n-t>
    </template>
    <p class="seat-change-description">
      {{ description }}
    </p>
    <billing-notice v-if="billing" class="seat-change-notice" v-bind="billing" />
    <seat-downgrade-notice v-if="!isUpgrade" :count="count" class="seat-change-notice" />
  </simple-base-dialog>
</template>

<style lang="scss" scoped>
.seat-change-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 7 * $bw;
  border-radius: 16px;

  &--upgrade {
    background: $c-seat-full-bg;
    color: $c-seat-full;
  }

  &--downgrade {
    background: $c-blue-light-4;
    color: $c-seat-signer;
  }

  :deep(.v-icon svg) {
    width: 100%;
    height: 100%;
  }
}

.seat-change-name {
  color: $c-grey-dark;
}

.seat-change-description {
  color: $c-skribbleu;
  line-height: 1.625rem;
}

.seat-change-notice {
  margin-top: 9 * $bw;
}
</style>
